import { useState, type ComponentProps } from 'react'

import { EventRsvpPicker, type EventRsvp } from '@components/molecules/EventRsvpPicker'

import type { ComponentDoc, PropDocs } from '../types'

type EventRsvpPickerProps = ComponentProps<typeof EventRsvpPicker>

const props: PropDocs<EventRsvpPickerProps> = {
  events: {
    type: 'Event[]',
    required: true,
    description:
      'Events from the eventsForRsvp query: { id, name, startTime, ianaTimeZone, isFull, hasRsvpFromSupporter, liveShifts }. A selected event lists its shifts beneath it.',
  },
  value: {
    type: 'EventRsvp[]',
    required: true,
    description:
      'Only the changes: { eventId, action: Register | Cancel, shiftIds? }. shiftIds is the full set of shifts wanted on that event. Unchanged events are left out.',
  },
  onChange: {
    type: '(value: EventRsvp[]) => void',
    required: true,
    description: 'Called with the new list of changes.',
  },
  disabled: { type: 'boolean', description: 'Turns the picker off. Use it while events load.' },
}

const EVENTS: EventRsvpPickerProps['events'] = [
  {
    id: '1',
    name: 'Saturday doorknock',
    startTime: '2026-09-21T10:00:00+10:00',
    ianaTimeZone: 'Australia/Melbourne',
    isFull: false,
    isPast: false,
    hasRsvpFromSupporter: true,
    liveShifts: [
      {
        id: '11',
        roleName: 'Door greeter',
        startTime: '2026-09-21T10:00:00+10:00',
        endTime: '2026-09-21T12:00:00+10:00',
        isFull: false,
        isAssignedToSupporter: true,
      },
      {
        id: '12',
        roleName: 'Runner',
        startTime: '2026-09-21T12:00:00+10:00',
        endTime: '2026-09-21T14:00:00+10:00',
        isFull: true,
        isAssignedToSupporter: false,
      },
    ],
  },
  {
    id: '2',
    name: 'Phone bank',
    startTime: '2026-09-24T18:00:00+10:00',
    ianaTimeZone: 'Australia/Melbourne',
    isFull: true,
    isPast: false,
    hasRsvpFromSupporter: false,
    liveShifts: [],
  },
  {
    id: '3',
    name: 'Market stall',
    startTime: '2026-09-28T08:00:00+10:00',
    ianaTimeZone: 'Australia/Melbourne',
    isFull: false,
    isPast: false,
    hasRsvpFromSupporter: false,
    liveShifts: [],
  },
  {
    id: '4',
    name: 'Launch rally',
    startTime: '2026-09-14T11:00:00+10:00',
    ianaTimeZone: 'Australia/Melbourne',
    isFull: true,
    isPast: true,
    hasRsvpFromSupporter: false,
    liveShifts: [],
  },
]

function Controlled() {
  const [value, setValue] = useState<EventRsvp[]>([])

  return (
    <div className="tw:flex tw:w-full tw:max-w-md tw:flex-col tw:gap-3">
      <EventRsvpPicker events={EVENTS} value={value} onChange={setValue} />
      <pre className="tw:m-0 tw:font-mono tw:text-xs tw:text-gray-cool-600">value = {JSON.stringify(value)}</pre>
    </div>
  )
}

const doc: ComponentDoc<EventRsvpPickerProps> = {
  slug: 'event-rsvp-picker',
  name: 'EventRsvpPicker',
  tier: 'molecule',
  description:
    'A MultiSelectField of events for one supporter, split into Upcoming and Past. Their current RSVPs start selected, and value holds only the changes, ready for logContact.',
  importStatement: "import { EventRsvpPicker } from '@components/molecules/EventRsvpPicker'",
  sourcePath: 'app/frontend/components/molecules/EventRsvpPicker.tsx',
  component: EventRsvpPicker,
  props,
  examples: [
    {
      title: 'Registered, full and open events',
      note: 'The doorknock starts selected because the supporter is registered, with their door greeter shift ticked beneath it. The full phone bank and the full runner shift are locked. Switch to Past to find the launch rally: it is full too, but a past event can always be recorded. A selection made under one timeframe survives switching to the other. Toggle events and shifts to see value list only the changes. The closed field counts the events selected and, when any are ticked, their shifts. Until something changes it reads RSVP\'d to and counts only upcoming events, so a long history of past RSVPs does not crowd the field. Once something changes it counts upcoming and past events together.',
      render: () => <Controlled />,
      code: `const [value, setValue] = useState<EventRsvp[]>([])

<EventRsvpPicker events={events} value={value} onChange={setValue} disabled={eventsQuery.isLoading} />`,
    },
  ],
  usage: {
    do: [
      'Load events with the eventsForRsvp query. Pass its loading state as disabled.',
      'Send value to logContact as eventRsvps. It is a list of changes, not the selection.',
      'Use its built-in empty states for no events and no search results.',
    ],
    dont: [
      'Read value as the current RSVPs. Unchanged events are not in it.',
      'Expect every full event or shift to be locked. A supporter already on one can still cancel or release it, and a past event is never locked.',
    ],
  },
  a11y: [
    'Works like MultiSelectField for keyboards and screen readers. There is no select all, so attendance is always recorded one event at a time.',
    'The Upcoming and Past toggle is a labelled group of buttons with aria-pressed.',
    'The Full badge is text, so locked events are not shown by colour alone.',
    'Each shift row is announced with its event name, role and time range.',
  ],
}

export default doc
